import { useState, useEffect } from "react";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import Pagination from "./Pagination"; // Adjust the path as needed

const DataTable = ({
  data,
  columns,
  selectable = true,
  onRowClick,
  searchable = true,
  itemsPerPage = 10,
  onSearch = null,
  totalPages: externalTotalPages = null,
  totalItems: externalTotalItems = null,
  currentPage: externalCurrentPage = null,
  onPageChange = null,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);

  const isServerSide = onSearch !== null || onPageChange !== null;
  const currentPage = externalCurrentPage !== null ? externalCurrentPage : internalCurrentPage;
  const setCurrentPage = onPageChange !== null ? onPageChange : setInternalCurrentPage;

  // Handle sorting
  const handleSort = (columnName) => {
    setSortConfig((prev) => ({
      key: columnName,
      direction: prev.key === columnName && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Filter and sort data
  const filteredSortedData = [...data]
    .filter((row) => {
      if (onSearch) return true; // Skip local filtering if server-side search is active
      if (!searchTerm) return true;
      return columns.some((col) => {
        const value = col.selector ? String(col.selector(row)) : "";
        return value.toLowerCase().includes(searchTerm.toLowerCase());
      });
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      const column = columns.find((col) => col.name === sortConfig.key);
      if (!column?.selector) return 0;
      const aValue = column.selector(a);
      const bValue = column.selector(b);
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });

  // Calculate total pages based on filtered data or external props
  const totalItems = externalTotalItems !== null ? externalTotalItems : filteredSortedData.length;
  const totalPages = externalTotalPages !== null ? externalTotalPages : Math.ceil(totalItems / itemsPerPage);

  // Use data directly if server-side, otherwise slice for local pagination
  const displayedData = isServerSide
    ? data
    : filteredSortedData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );

  // Handle row selection
  const toggleRow = (row) => {
    const newSelected = selectedRows.includes(row)
      ? selectedRows.filter((r) => r !== row)
      : [...selectedRows, row];
    setSelectedRows(newSelected);
  };

  // Handle select all
  const toggleAllRows = () => {
    const newSelected = selectedRows.length === data.length ? [] : [...data];
    setSelectedRows(newSelected);
  };

  // Debounce effect for search


  useEffect(() => {
    if (onSearch) {
      const handler = setTimeout(() => {
        onSearch(searchTerm);
      }, 500); // 500ms debounce

      return () => {
        clearTimeout(handler);
      };
    }
  }, [searchTerm, onSearch]);

  return (
    <div className="flex flex-col gap-4 p-4 w-full h-full min-w-0 min-h-0 max-w-full overflow-hidden">
      {searchable && (
        <div className="flex-shrink-0 w-full max-w-xs relative">
          <input
            type="text"
            placeholder="Search..."
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-800 pr-10"
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTerm(value);
              if (!onSearch) setCurrentPage(1); // Only reset local page here, server side handled in parent via effect
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {/* Table Container */}
      <div className="flex flex-col flex-1 rounded-lg border border-gray-200 overflow-hidden min-h-0">
        <div className="flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-gray-200 bg-white table-auto">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                {selectable && (
                  <th className="w-12 px-4 py-3 bg-gray-50">
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={selectedRows.length === data.length && data.length > 0}
                      onChange={toggleAllRows}
                      aria-label="Select all"
                    />
                  </th>
                )}
                {columns.map((column) => (
                  <th
                    key={column.name}
                    className={`px-4 py-3 text-left text-sm font-medium text-gray-700 bg-gray-50 ${column.sortable ? "cursor-pointer hover:bg-gray-100" : ""
                      }`}
                    onClick={() => column.sortable && handleSort(column.name)}
                  >
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      {column.name}
                      {column.sortable && (
                        <span className="flex flex-col">
                          <ChevronUpIcon
                            className={`w-4 h-4 ${sortConfig.key === column.name && sortConfig.direction === "asc"
                              ? "text-blue-600"
                              : "text-gray-400"
                              }`}
                          />
                          <ChevronDownIcon
                            className={`w-4 h-4 -mt-1 ${sortConfig.key === column.name && sortConfig.direction === "desc"
                              ? "text-blue-600"
                              : "text-gray-400"
                              }`}
                          />
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {displayedData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`hover:bg-gray-50 ${onRowClick ? "cursor-pointer" : ""}`}
                  onClick={(e) => {
                    // Prevent row click if an interactive element is clicked
                    const interactiveElements = ["button", "input", "a", "[role='button']"];
                    const targetElement = e.target.closest(interactiveElements.join(", "));
                    if (!targetElement) {
                      onRowClick?.(row);
                    }
                  }}
                >
                  {selectable && (
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={selectedRows.includes(row)}
                        onChange={() => toggleRow(row)}
                        aria-label={`Select row ${rowIndex + 1}`}
                      />
                    </td>
                  )}
                  {columns.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      className={`px-4 py-2 text-sm whitespace-nowrap ${column.align === "center" ? "text-center" : "text-left"
                        }`}
                    >
                      {column.cell
                        ? column.cell(row)
                        : column.selector
                          ? column.selector(row)
                          : "N/A"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex-shrink-0">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
        />
      </div>
    </div>
  );
};

export default DataTable;
